<script lang="ts">
	//Hooks
	import { createEventDispatcher, onMount } from 'svelte';
	//Formatting
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		Alert,
		Button,
		Container,
		Input,
		Modal,
		SelectCard,
		Text,
		Toggle,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
		CardGrid,
		type SelectOptionPropsType,
	} from '@Shelly/shelly-design-system';
	//Components
	import DoubleTimePicker from '../../../TimePicker/DoubleTimePicker.svelte';
	import WebhookConditions from './Conditions/WebhookConditions.svelte';
	//Helpers
	import { getWebHookEvents } from 'src/helpers/actions/webhooks/core/webhook_helpers';
	import {
		getWizardItems,
		DEVICES_WITHOUT_ACTIVE_TIME_STEP,
	} from 'src/helpers/actions/webhooks/view/webhook_view_helpers';
	import { deepCopy, isValidURL } from 'src/helpers/utilities';
	import {
		buildCreateWebhookCondition,
		buildCreateWebhookData,
		getCreateWebhookDisabledConditions,
		getCreateWebhookWizardStepIndexes,
		hasValidCreateWebhookConditions,
		hasValidCreateWebhookUrls,
	} from 'src/helpers/actions/webhooks/view/create_webhook_save_helpers';
	import { isCamera } from 'src/helpers/interface_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import WebHooksActions from './Actions/WebHooksActions.svelte';
	import ExitConfirmationModal from 'src/components/ExitConfirmationModal.svelte';
	import CameraWebhookConditions from './Conditions/CameraWebhookConditions.svelte';
	import LnmWebhookConditions from './Conditions/LnmWebhookConditions.svelte';
	//Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	// Stores
	//Types
	import type { Device } from 'src/types/Device';
	import type {
		WebHookEventOption,
		WebHooksParams,
		WebHooksRequest,
		WebHookSupportedList,
	} from 'src/types/requests/settings/webhooks';
	// Stores
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let device: Device;
	export let showWizard: boolean;
	export let peripherals: any[] = null;
	export let actionProps: WebHooksParams = null;
	export let supportedActionsList: WebHookSupportedList;

	const closeModalTimeout = 300;
	const dispatch = createEventDispatcher();
	const components = actionProps.components;
	const hasComponents = components != null;
	const hasPeripherals = peripherals != null;
	const hideTimePicker = DEVICES_WITHOUT_ACTIVE_TIME_STEP.includes(device.type);
	const selectCameraComponentInCondition = isCamera(device.type) && hasComponents;
	const selectLnmComponentInCondition =
		(components || []).length > 0 && components.every((item) => item.type === 'lnm');
	const selectComponentInCondition = selectCameraComponentInCondition || selectLnmComponentInCondition;

	let wizardComponentType: 'peripheral' | 'component' | null = null;
	if (hasPeripherals) {
		wizardComponentType = 'peripheral';
	} else if (components && !selectComponentInCondition) {
		wizardComponentType = 'component';
	}

	const wizardItems = getWizardItems(wizardComponentType, hideTimePicker),
		title = actionProps?.id
			? $format('edit_action', { default: 'Edit action' })
			: $format('create_action', { default: 'Create action' }),
		errors = {
			name: {
				show: false,
				label: $format('enter_web_name', {
					default: 'Please set an action name!',
				}),
			},
			condition: {
				show: false,
				label: $format('field_cannot_be_empty', {
					default: 'This field cannot be empty',
				}),
			},
			urls: {
				show: false,
				required: $format('field_url_cannot_be_empty', {
					default: 'Invalid argument URLs: At least one URL is required!',
				}),
				invalid: $format('enter_valid_url', {
					default: 'Invalid URLs: Please enter a valid URL!',
				}),
			},
		};

	const onPeripheralChange = (index: number) => {
		peripheralsIndex = index;
		const peripheral = peripherals[index];
		actionProps.cid = peripheral.sensor.extid;
		actionProps.inputType = peripheral.sensor.inputType;
		actionProps.type = peripheral.sensor.type;
		actionProps.unit = peripheral.sensor.unit;
		actionProps.icon = peripheral.sensor.icon;
		updateEventsOptions();
	};

	const onComponentsChange = (index: number) => {
		componentIndex = index;
		actionProps = { ...actionProps, ...components[index] };
		updateEventsOptions();
	};

	const updateEventsOptions = () => {
		const events = getWebHookEvents(actionProps, supportedActionsList, device);
		eventsOptions = events.options;
		eventsIndex = eventsOptions.length === 1 ? 0 : events.index;
	};

	const changeEvent = (e: CustomEvent) => {
		eventsIndex = e.detail.value;
		actionProps.event = e.detail.event;
	};

	const getWizardStepIndexes = () => {
		return getCreateWebhookWizardStepIndexes(chooseComponent, hideTimePicker);
	};

	const isTimePickerStep = (index: number): boolean => {
		return getWizardStepIndexes().time === index;
	};

	const isConditionStep = (index: number): boolean => {
		return getWizardStepIndexes().condition === index;
	};

	const isActionsStep = (index: number): boolean => {
		return getWizardStepIndexes().actions === index;
	};

	const isComponentStep = (index: number): boolean => {
		return getWizardStepIndexes().component === index;
	};

	const isNameStep = (index: number): boolean => {
		return getWizardStepIndexes().name === index;
	};

	const getDisabledConditions = (
		isNameEmpty: boolean,
		hasValidConditions: boolean,
		hasValidUrls: boolean,
	): boolean[] => {
		const stepIndexes = getWizardStepIndexes();
		return getCreateWebhookDisabledConditions(
			wizardItems.length,
			stepIndexes,
			isNameEmpty,
			hasValidConditions,
			hasValidUrls,
		);
	};

	const getConditionStepIndex = (): number => getWizardStepIndexes().condition;

	const showNameError = () => {
		errors.name.show = true;
		activeStep = wizardItems[getWizardStepIndexes().name];
	};

	const showConditionError = () => {
		errors.condition.show = true;
		activeStep = wizardItems[getConditionStepIndex()];
	};

	const applyWebhookConditions = (data: WebHooksParams, eventProps: WebHookEventOption): boolean => {
		const conditionResult = buildCreateWebhookCondition(eventProps);
		if (!conditionResult.isValid) {
			showConditionError();
			return false;
		}
		if (conditionResult.condition) {
			data.condition = conditionResult.condition;
		}
		return true;
	};

	const submitWebhook = async (data: WebHooksParams) => {
		waitForResponse = true;
		const res: WebHooksRequest = await gen2Request(
			device,
			`webhook.${data.id ? 'update' : 'create'}`,
			data,
		);
		if (res?.isok) {
			// Give some time before the next request to ensure the webhooks list is updated
			showWizard = false;
			setTimeout(() => {
				dispatch('save', data);
			}, closeModalTimeout);
		}
		waitForResponse = false;
		onSettingsResponse(res, device, {
			success: $format('action_saved_scc_toast', {
				default: 'Action successfully saved!',
			}),
		});
	};

	const save = async () => {
		const eventProps = eventsOptions[eventsIndex];
		const data = buildCreateWebhookData(
			actionProps,
			eventProps,
			device,
			hideTimePicker,
			hasPeripherals,
		);
		const selectedLnmComponent = components?.[componentIndex];
		if (eventProps?.event?.startsWith('lnm.') && selectedLnmComponent?.type === 'lnm') {
			data.cid = selectedLnmComponent.cid;
		}

		if (!data.name) {
			showNameError();
			return;
		}
		if (!applyWebhookConditions(data, eventProps)) {
			return;
		}
		if (!hasValidCreateWebhookUrls(data.urls, isValidURL)) {
			errors.urls.show = true;
			return;
		}
		await submitWebhook(data);
	};

	const keyPress = (e) => {
		e.stopImmediatePropagation();
	};

	const peripheralsOptions: SelectOptionPropsType[] = [];
	const componentsOptions: SelectOptionPropsType[] = [];

	let showModal = false;
	let showExitConfirmationModal = false;
	let exitConfirmationModalState = false;
	let clonedActionObject: WebHooksParams;
	let chooseComponent = hasPeripherals || (hasComponents && !selectComponentInCondition);
	let activeStep = wizardItems[0];
	let waitForResponse = false;
	let peripheralsIndex = 0;
	let componentIndex = 0;
	let eventsIndex = -1;
	let isInputDirty = false;
	let disabled = false;
	let eventsOptions: WebHookEventOption[];
	let disabledConditions: boolean[] = [];

	actionProps.set_active_time =
		actionProps.active_between != null && actionProps.active_between?.length > 0;
	if (!actionProps.active_between || (actionProps.active_between.length === 0 && !hideTimePicker)) {
		actionProps.active_between = ['00:00', '00:00'];
	}
	if (!actionProps.urls) {
		actionProps.urls = [];
	}
	if (peripherals) {
		peripherals.forEach((el, i) => {
			peripheralsOptions.push({
				value: i,
				title: el.sensor.name || el.sensor.alias,
			});
			if (actionProps.id != null && el.sensor.component === actionProps.component) {
				peripheralsIndex = i;
			}
		});
	} else if (components) {
		components.forEach((el, i) => {
			componentsOptions.push({
				value: i,
				title: el.title,
			});
			if (
				actionProps.id != null &&
				el.component === actionProps.event.split('.')[0] + ':' + actionProps.cid
			) {
				onComponentsChange(i);
			} else if (actionProps.id == null && i === 0) {
				onComponentsChange(0);
			}
		});
	}

	updateEventsOptions();
	clonedActionObject = deepCopy(actionProps);
	onMount(() => (showModal = true));

	$: isLastStepWizard = activeStep.activeIndex === wizardItems.length - 1;
	$: {
		const isNameEmpty = !actionProps.name || actionProps.name.trim() === '';
		const hasValidConditions =
			eventsIndex !== -1 && hasValidCreateWebhookConditions(eventsOptions[eventsIndex]);
		const hasValidUrls = actionProps.urls.length > 0 && actionProps.urls[0] !== '';

		disabledConditions = getDisabledConditions(isNameEmpty, hasValidConditions, hasValidUrls);
		disabled = disabledConditions[activeStep.activeIndex];
	}

	$: if (showModal) {
		window.addEventListener('keydown', keyPress);
	} else {
		window.removeEventListener('keydown', keyPress);
	}
</script>

<Modal
	{title}
	{showModal}
	on:close={() => {
		if (JSON.stringify(actionProps) !== JSON.stringify(clonedActionObject)) {
			showExitConfirmationModal = true;
			exitConfirmationModalState = false;
		} else {
			showModal = false;
			setTimeout(() => (showWizard = false), 300);
		}
	}}
	removeScrollPadding
>
	<Wizard
		slot="toolbar"
		activeStepNumber={activeStep.activeIndex}
		stepsTotal={wizardItems.length}
		activeStepTitle={activeStep.title}
	>
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={() => {
					activeStep = wizardItems[i];
				}}
				active={activeStep.activeIndex === i}
				title={item.title}
				disabled={disabledConditions[i - 1]}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<div class:flex-overflow-hidden={false} slot="content">
		<WizardContainer activeStepIndex={activeStep.activeIndex} adaptiveHeight={true}>
			{#each wizardItems as item, i (i)}
				<WizardContent active={activeStep.activeIndex === i} class={'pl-0 pr-0 pb-s'}>
					{@const isActiveTimeStep = isTimePickerStep(i)}
					{@const isActiveConditionStep = isConditionStep(i)}
					{@const isActiveActionsStep = isActionsStep(i)}
					{@const isActiveComponentStep = isComponentStep(i)}
					{@const isActiveNameStep = isNameStep(i)}
					{#if isActiveTimeStep}
						<Container padding="scrollPaddings">
							<Alert
								class="mb-m"
								type="info"
								heading={$format('alert_message_always_active_action', {
									default:
										'The action will be always active unless you want to set custom active time to best suit your preferences.',
								})}
							/>
						</Container>
					{:else if isActiveConditionStep && !selectComponentInCondition}
						<Container padding="scrollPaddings">
							<Alert
								class="mb-m"
								type="info"
								heading={$format('alert_message_threshold_trigger_event', {
									default:
										'The event would trigger when the configured threshold is reached.',
								})}
							/>
						</Container>
					{:else if isActiveActionsStep}
						<Container padding="scrollPaddings">
							<Alert
								class="mb-m"
								heading={actionProps.urls.length >= 5
									? $format('alert_warning_heading_actions_limit_reached', {
											default: 'Actions limit reached',
										})
									: $format('alert_info_heading_add_5_actions', {
											default: 'You can add up to 5 actions.',
										})}
								isHeadingBold={actionProps.urls.length >= 5}
								message={actionProps.urls.length >= 5
									? $format('alert_warning_message_actions_limit_description', {
											default:
												'You have reached the limit of 5 actions. If you want to add a new one, please delete one of the actions you have already added.',
										})
									: ''}
								type={actionProps.urls.length >= 5 ? 'warning' : 'info'}
							/>
						</Container>
					{/if}

					<Container noScroll padding="scrollPaddings">
						{#if activeStep.activeIndex !== wizardItems.length - 1 && !isActiveTimeStep && item.subtitle}
							<Text element="h2" class="mb-s">
								{item.subtitle}
							</Text>
						{/if}
						{#if hideTimePicker && i === 0}
							<Alert
								class="mb-m"
								type="info"
								isHeadingBold
								heading={$format('battery_life_inform', {
									default: 'Battery life information',
								})}
								message={$format('action_warning', {
									default:
										'Hooks in general, reduce battery life. Particularly, HTTPS hooks shortens battery life in half.',
								})}
							/>
						{/if}
						{#if peripherals && isActiveComponentStep}
							<CardGrid gridGap="s" lg={290} sm={270} noPaddingBottom>
								{#each peripheralsOptions as option}
									<SelectCard
										cardBgColor={$showNewLayout ? 'color-1' : undefined}
										class="width-full"
										type="horizontal"
										variant="radio"
										title={option.title}
										selected={peripheralsIndex === option.value}
										on:click={() => onPeripheralChange(Number(option.value))}
									/>
								{/each}
							</CardGrid>
						{:else if chooseComponent && components && isActiveComponentStep}
							<CardGrid gridGap="s" lg={290} sm={270} noPaddingBottom>
								{#each componentsOptions as option}
									<SelectCard
										cardBgColor={$showNewLayout ? 'color-1' : undefined}
										class="width-full"
										type="horizontal"
										variant="radio"
										title={option.title}
										selected={componentIndex === option.value}
										on:click={() => onComponentsChange(Number(option.value))}
									/>
								{/each}
							</CardGrid>
						{:else if isActiveNameStep}
							<Input
								aria-labelledby="input-2uncx8h"
								size="m"
								type="text"
								icon="fas fa-link"
								tabIndex="-1"
								error={actionProps.name === '' && isInputDirty ? errors.name.label : ''}
								bind:value={actionProps.name}
								label={$format('label_name', {
									default: 'Name',
								})}
								on:blur={() => {
									isInputDirty = true;
								}}
								id="input-2uncx8h"
							/>
						{:else if isActiveTimeStep}
							<Toggle
								size="l"
								bind:checked={actionProps.set_active_time}
								id="toggle-o3q954n"
							>
								{$format('modal_webhooks_set_active_time', {
									default: 'Set active time',
								})}
							</Toggle>
							{#if actionProps.set_active_time}
								<DoubleTimePicker
									bind:time={actionProps.active_between}
									class="mt-m"
									slot="bottom"
								/>
							{/if}
						{:else if isActiveConditionStep}
							{#if selectCameraComponentInCondition}
								<CameraWebhookConditions
									bind:actionProps
									{components}
									bind:componentIndex
									bind:eventsOptions
									bind:eventsIndex
									{supportedActionsList}
									{device}
								/>
							{:else if selectLnmComponentInCondition}
								<LnmWebhookConditions
									bind:actionProps
									{components}
									bind:componentIndex
									bind:eventsOptions
									bind:eventsIndex
									{supportedActionsList}
									{device}
									error={errors.condition}
								/>
							{:else}
								<WebhookConditions
									{actionProps}
									bind:eventsOptions
									bind:eventsIndex
									error={errors.condition}
									on:change={changeEvent}
								/>
							{/if}
						{:else if isActiveActionsStep}
							<WebHooksActions
								{device}
								bind:urls={actionProps.urls}
								error={errors.urls}
								tokens={eventsOptions[eventsIndex]?.tokens}
							/>
						{/if}
					</Container>
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>
	<div slot="footer" class="d-flex justify-content-end">
		{#if activeStep.activeIndex > 0}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => {
					if (activeStep.activeIndex > 0) {
						activeStep = wizardItems[activeStep.activeIndex - 1];
					}
				}}
			>
				{$format('button_back', { default: 'Back' })}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={isLastStepWizard ? 'fas fa-save' : ''}
			rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
			responsiveSize={{
				sm: 'm',
			}}
			loading={waitForResponse}
			disabled={components?.length === 0 || disabled}
			on:click={() => {
				if (isLastStepWizard) {
					save();
				} else {
					activeStep = wizardItems[activeStep.activeIndex + 1];
				}
			}}
		>
			{isLastStepWizard
				? $format('button_save', { default: 'Save' })
				: $format('button_next', { default: 'Next' })}
		</Button>
	</div>
</Modal>

<ExitConfirmationModal
	type={$format('the_action', { default: 'the action' })}
	{exitConfirmationModalState}
	{showExitConfirmationModal}
	showAddEditModal={showModal}
	on:close={(e) => {
		exitConfirmationModalState = e.detail.exitConfirmationModalState;
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
	}}
	on:confirm={(e) => {
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
		showWizard = e.detail.showAddEditModal;
		showModal = e.detail.showAddEditModal;
	}}
/>

<style>
	:global(.double-time-picker-disabled) {
		opacity: 0.5;
		pointer-events: none;
	}
</style>
